/* eslint-disable react/no-unused-prop-types */
import type {NavigateFunction} from 'react-router'
import {fromMaybe, maybe} from '@freckle/maybe'
import type {LinksT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {
  validateGrade,
  gradeIntAsString,
  gradeStringToInt,
} from '@freckle/educator-entities/ts/common/helpers/common-helper'
import {useTeachersAsASchoolAdmin} from '@freckle/educator-entities/ts/common/hooks/use-teacher-as-a-school-admin'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {
  Table,
  cellFromRow,
  aligned,
  disableSortBy,
  idAccessorFromProp,
} from '@freckle/educator-materials/ts/components/table'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {
  LinkedGrade,
  LinkedCourseCount,
  Idp,
  LinkedTeacherCount,
  SisId,
} from '@freckle/school/ts/common/components/table/cell'
import {WithResources} from '@freckle/school/ts/common/components/with-resources'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import {Routes, STUDENT_FILTER_QUERY_PARAM_TAG} from '@freckle/school/ts/common/routers/routes'
import type {StudentTabFiltersQueryParamsT} from '@freckle/school/ts/common/routers/routes'
import {fetchStudentTableRows} from '@freckle/school/ts/manage-teachers/api-helpers/api-student-table'
import type {IdentityProviderOrNoneT} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import {ApiControlledTabContainer} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import type {TableParamsT} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import {FilterInput} from '@freckle/school/ts/manage-teachers/components/filter-input'
import type {
  CourseSimplifiedT,
  TeacherSimplifiedT,
} from '@freckle/school/ts/manage-teachers/models/student-table-row'
import {SourceDropdown} from './../source-dropdown'
import StudentTabFilter from './student-tab-filter'
import {headerVerticalAlign} from './student-tab.module.scss'

export type StudentFilterTagT = 'student-filters'

export type StudentTabFilteringT = {
  tag: StudentFilterTagT
  course: CourseSimplifiedT | undefined | null
  grade: number | undefined | null
  teacher: TeacherSimplifiedT | undefined | null
  source: 'admin' | 'non-admin' | undefined | null
  studentName: string | undefined | null
  sisId: number | undefined | null
  teacherName: string | undefined | null
  identityProviders: Array<IdentityProviderOrNoneT>
}

type TableRowT = {
  id: number
  name: string
  sisId: string | undefined | null
  grade: number
  teachers: Array<BaseTeacherAttrs>
  coursesCount: number
  source: string | undefined | null
  identityProvider: IdentityProviderT | undefined | null
}

export type Props = {
  currentSchoolIds: Array<number> | undefined | null
  schools: Array<SchoolAttrs>
  routerHistory: NavigateFunction
  tableParams: TableParamsT<StudentTabFilteringT>
  summary: SummaryT
}

export function StudentTabContainer(props: Props) {
  const teachers = useTeachersAsASchoolAdmin()
  return (
    <WithResources
      resources={{teachers}}
      render={({teachers}) => <StudentTab {...props} teachers={teachers} />}
    />
  )
}

type StudentTabProps = Props & {teachers: BaseTeacherAttrs[]}

function StudentTab(props: StudentTabProps) {
  const {currentSchoolIds, schools, teachers, tableParams, routerHistory, summary} = props
  const {t} = useSafeTranslation()

  function fetchTableRows(): (newTableParams: TableParamsT<StudentTabFilteringT>) => Promise<{
    links: LinksT
    rows: Array<TableRowT>
    totalCount: number
  }> {
    return newTableParams => {
      const allSchoolIds = schools.map(s => s.id)
      const schoolIdsToUse = fromMaybe(() => allSchoolIds, currentSchoolIds)

      return fetchStudentTableRows(schoolIdsToUse, newTableParams).then(
        ({studentTableRows, links, totalCount}) => {
          const rows = studentTableRows.map(studentTableRow => {
            const {firstName, lastName, teacherIds, ...rest} = studentTableRow

            const name = t('MANAGE_TEACHERS_STUDENT_TAB_TEACHER_FULL_NAME', {firstName, lastName})
            const teachersOfStudent = teachers.filter(teacher => teacherIds.includes(teacher.id))

            return {
              name,
              teachers: teachersOfStudent,
              ...rest,
            }
          })

          return {links, rows, totalCount}
        },
      )
    }
  }

  function onUpdate(newTableParams: TableParamsT<StudentTabFilteringT>) {
    const {additionalFilters} = newTableParams

    const url = Routes.dashboard({
      schoolIds: currentSchoolIds,
      activeTab: 'student-tab',
      position: newTableParams.position,
      searchValue: newTableParams.searchValue,
      limit: newTableParams.limit,
      additionalFilters: buildStudentTabFiltersQueryParams(newTableParams, additionalFilters),
    })
    routerHistory(url)
  }

  const {additionalFilters} = tableParams

  const filtersNode = (
    <StudentTabFilter studentTabFilter={additionalFilters} currentSchoolIds={currentSchoolIds} />
  )

  const initialFilters = getInitialFilters(tableParams)

  // Preserve pagination limit
  const paginationLimit = tableParams.limit !== null ? {limit: tableParams.limit} : {}

  return (
    <>
      <ApiControlledTabContainer
        name="student"
        tableParams={tableParams}
        routerHistory={routerHistory}
        filters={filtersNode}
        fetchRows={fetchTableRows()}
        onUpdate={onUpdate}
        render={({paginationPanel: PaginationPanel}, rows) => (
          <>
            <Table
              data={rows}
              columns={[
                {
                  Header: (
                    <>
                      <Text style="label-1">
                        {t('MANAGE_TEACHERS_STUDENT_TAB_STUDENT_TABLE_STUDENT_NAME_HEADER')}
                      </Text>
                      <FilterInput
                        initValue={initialFilters.studentName}
                        routeFn={studentName =>
                          routerHistory(
                            Routes.dashboard({
                              schoolIds: currentSchoolIds,
                              activeTab: 'student-tab',
                              additionalFilters: {
                                tag: STUDENT_FILTER_QUERY_PARAM_TAG,
                                studentName,
                              },
                              ...paginationLimit,
                            }),
                          )
                        }
                      />
                    </>
                  ),
                  ...idAccessorFromProp<TableRowT>('name'),
                  widthPx: 200,
                },
                {
                  Header: (
                    <>
                      <Text style="label-1">
                        {t('MANAGE_TEACHERS_STUDENT_TAB_STUDENT_TABLE_SIS_ID_HEADER')}
                      </Text>
                      <FilterInput
                        initValue={initialFilters.sisId}
                        routeFn={sisId =>
                          routerHistory(
                            Routes.dashboard({
                              schoolIds: currentSchoolIds,
                              activeTab: 'student-tab',
                              additionalFilters: {
                                tag: STUDENT_FILTER_QUERY_PARAM_TAG,
                                sisId,
                              },
                              ...paginationLimit,
                            }),
                          )
                        }
                      />
                    </>
                  ),
                  Cell: cellFromRow(({sisId}: TableRowT) => <SisId sisId={sisId} />),
                  ...idAccessorFromProp<TableRowT>('sisId'),
                  widthPx: 100,
                },
                {
                  Header: (
                    <>
                      <Text style="label-1">
                        {t('MANAGE_TEACHERS_STUDENT_TAB_STUDENT_TABLE_GRADE_HEADER')}
                      </Text>
                      <FilterInput
                        initValue={initialFilters.studentGrade}
                        routeFn={grade =>
                          routerHistory(
                            Routes.dashboard({
                              schoolIds: currentSchoolIds,
                              activeTab: 'student-tab',
                              additionalFilters: {
                                tag: STUDENT_FILTER_QUERY_PARAM_TAG,
                                grade: gradeStringToInt(grade),
                              },
                              ...paginationLimit,
                            }),
                          )
                        }
                        parseInput={s => gradeIntAsString(gradeStringToInt(s))}
                        validateInput={validateGrade}
                      />
                    </>
                  ),
                  Cell: cellFromRow(({grade}: TableRowT) => (
                    <LinkedGrade grade={grade} currentSchoolIds={currentSchoolIds} />
                  )),
                  ...idAccessorFromProp<TableRowT>('grade'),
                  widthPx: 60,
                },
                {
                  Header: (
                    <>
                      <Text style="label-1">
                        {t('MANAGE_TEACHERS_STUDENT_TAB_STUDENT_TABLE_TEACHER_NAME_HEADER')}
                      </Text>
                      <FilterInput
                        initValue={initialFilters.teacherName}
                        routeFn={teacherName =>
                          routerHistory(
                            Routes.dashboard({
                              schoolIds: currentSchoolIds,
                              activeTab: 'student-tab',
                              additionalFilters: {
                                tag: STUDENT_FILTER_QUERY_PARAM_TAG,
                                teacherName,
                              },
                              ...paginationLimit,
                            }),
                          )
                        }
                      />
                    </>
                  ),
                  Cell: cellFromRow((row: TableRowT) => <LinkedTeacherCount row={row} />),
                  ...idAccessorFromProp<TableRowT>('teachers'),
                  widthPx: 130,
                },
                {
                  Header: (
                    <>
                      <Text style="label-1" className={headerVerticalAlign}>
                        {t('MANAGE_TEACHERS_STUDENT_TAB_STUDENT_TABLE_COURSES_HEADER')}
                      </Text>
                    </>
                  ),
                  Cell: cellFromRow((row: TableRowT) => <LinkedCourseCount row={row} />),
                  ...idAccessorFromProp<TableRowT>('coursesCount'),
                  widthPx: 50,
                },
                {
                  Header: (
                    <SourceDropdown
                      id="dashboard-students-source-selector"
                      initSources={initialFilters.sources}
                      summary={{tag: 'for-student', ...summary}}
                      routeFn={identityProviders =>
                        routerHistory(
                          Routes.dashboard({
                            schoolIds: currentSchoolIds,
                            activeTab: 'student-tab',
                            additionalFilters: {
                              tag: STUDENT_FILTER_QUERY_PARAM_TAG,
                              identityProviders,
                            },
                            ...paginationLimit,
                          }),
                        )
                      }
                      labelText={t('MANAGE_TEACHERS_STUDENT_TAB_STUDENT_TABLE_ROSTERED_BY_HEADER')}
                    />
                  ),
                  Cell: cellFromRow(({identityProvider}: TableRowT) => (
                    <Idp source={identityProvider} />
                  )),
                  ...idAccessorFromProp<TableRowT>('identityProvider'),
                  widthPx: 150,
                },
              ].map(c => ({...aligned('left'), ...c, disableSortBy}))}
            />
            <PaginationPanel components={{sizePerPageDropdown: <></>, pageList: <></>}} />
          </>
        )}
      />
    </>
  )
}

function buildStudentTabFiltersQueryParams(
  tableParams: TableParamsT<StudentTabFilteringT>,
  additionalFilters?: StudentTabFilteringT | null,
): StudentTabFiltersQueryParamsT | undefined | null {
  if (additionalFilters === null || additionalFilters === undefined) {
    return null
  }
  const initialFilters = getInitialFilters(tableParams)
  return {
    tag: STUDENT_FILTER_QUERY_PARAM_TAG,
    courseId: additionalFilters.course && additionalFilters.course.id,
    courseName: additionalFilters.course && additionalFilters.course.name,
    teacherId: additionalFilters.teacher && additionalFilters.teacher.id,
    teacherName:
      (additionalFilters.teacher && additionalFilters.teacher.name) ?? initialFilters.teacherName,
    grade: additionalFilters.grade ?? gradeStringToInt(initialFilters.studentGrade),
    source: additionalFilters.source,
    studentName: initialFilters.studentName,
    sisId: initialFilters.sisId,
  }
}

type InitialFiltersT = {
  studentName: string
  sisId: string
  studentGrade: string
  teacherName: string
  sources: Array<IdentityProviderOrNoneT>
}

function getInitialFilters(tableParams: TableParamsT<StudentTabFilteringT>): InitialFiltersT {
  const {additionalFilters} = tableParams

  const empty = () => ''
  const emptyArray = () => []

  return {
    studentName: maybe(empty, filters => fromMaybe(empty, filters.studentName), additionalFilters),
    sisId: maybe(
      empty,
      filters => maybe(empty, id => id.toString(), filters.sisId),
      additionalFilters,
    ),
    studentGrade: maybe(
      empty,
      filters => maybe(empty, g => gradeIntAsString(g), filters.grade),
      additionalFilters,
    ),
    teacherName: maybe(empty, filters => fromMaybe(empty, filters.teacherName), additionalFilters),
    sources: maybe(
      emptyArray,
      filters => fromMaybe(emptyArray, filters.identityProviders),
      additionalFilters,
    ),
  }
}
